import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SupplierDashboard from './SupplierDashboard';
import BuyerDashboard from './BuyerDashboard';
import RepDashboard from './RepDashboard';
import AdminDashboard from './AdminDashboard';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, token, loading, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Handle payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');

    if (sessionId && payment === 'success' && token) {
      setCheckingPayment(true);
      // Poll for payment status
      const pollPayment = async (attempts = 0) => {
        if (attempts >= 5) {
          setPaymentStatus('timeout');
          setCheckingPayment(false);
          return;
        }

        try {
          const res = await fetch(`${API}/payments/status/${sessionId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();

          if (data.payment_status === 'paid') {
            setPaymentStatus('success');
            setCheckingPayment(false);
            refreshUser(); // Refresh user to get updated membership status
            return;
          } else if (data.status === 'expired') {
            setPaymentStatus('expired');
            setCheckingPayment(false);
            return;
          }

          // Continue polling
          setTimeout(() => pollPayment(attempts + 1), 2000);
        } catch (err) {
          console.error('Payment check error:', err);
          setTimeout(() => pollPayment(attempts + 1), 2000);
        }
      };

      pollPayment();
    } else if (payment === 'cancelled') {
      setPaymentStatus('cancelled');
    }
  }, [searchParams, token, refreshUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-copper" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show payment status message
  const PaymentMessage = () => {
    if (checkingPayment) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span className="text-blue-800">Verifying payment...</span>
        </div>
      );
    }

    if (paymentStatus === 'success') {
      return (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
          <span className="text-green-800 font-medium">
            Payment successful! Your membership is now active.
          </span>
        </div>
      );
    }

    if (paymentStatus === 'cancelled') {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
          <span className="text-yellow-800">
            Payment was cancelled. You can try again anytime.
          </span>
        </div>
      );
    }

    return null;
  };

  // Render dashboard based on role
  const renderDashboard = () => {
    switch (user.role) {
      case 'Supplier':
        return <SupplierDashboard PaymentMessage={PaymentMessage} />;
      case 'Buyer':
        return <BuyerDashboard PaymentMessage={PaymentMessage} />;
      case 'Rep':
        return <RepDashboard PaymentMessage={PaymentMessage} />;
      case 'Admin':
        return <AdminDashboard />;
      default:
        return (
          <div className="min-h-screen bg-slate-50 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-navy mb-2">Unknown Role</h1>
              <p className="text-slate-600">Your account role is not recognized.</p>
            </div>
          </div>
        );
    }
  };

  return renderDashboard();
}
